import Vue from 'vue'
import App from './App.vue'
import "./filters_.js";
import VueProgressBar from 'vue-progressbar'

// VUEX - https://vuex.vuejs.org/
import store from './store'

// VUE-ROUTER - https://router.vuejs.org/
import router from './router'

// PLUGINS
import vuetify from './plugins/vuetify'
import i18n from './plugins/vue-i18n'
import './plugins/vue-google-maps'
import './plugins/vue-shortkey'
import './plugins/vue-head'
import './plugins/vue-gtag'
import './plugins/apexcharts'
import './plugins/echarts'
import './plugins/animate'
import './plugins/clipboard'
import './plugins/moment'

// FILTERS
import './filters/capitalize'
import './filters/lowercase'
import './filters/uppercase'
import './filters/formatCurrency'
import './filters/formatDate'

// STYLES
// Main Theme SCSS
import './assets/scss/theme.scss'

// Animation library - https://animate.style/
import 'animate.css/animate.min.css'

import './registerServiceWorker';
import { installChunkReloadGuard, clearChunkReloadGuardOnSuccess } from './utils/chunkReloadGuard'
import { startVersionCheck } from './utils/versionCheck'

// Se instala antes de crear la instancia de Vue para capturar fallos de carga
// de chunks lazy que ocurran durante el arranque.
installChunkReloadGuard()


// Usar sentry 
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";

/**
 * Inicialización de Sentry para el monitoreo de errores y rendimiento.
 */
Sentry.init({
    Vue, // Instancia de Vue para que Sentry capture errores de componentes.

    dsn: process.env.VUE_APP_SENTRY_DSN || null,

    environment: process.env.NODE_ENV, // deberia ayudar a filtrar errores de local vs servidores reales.

    /** * @type {Array} Extensiones activas de Sentry.
     * En este caso, BrowserTracing permite medir tiempos de carga y navegación.
     */
    integrations: [
        new BrowserTracing({
            /** * @type {object} Vincula Sentry con vue-router para registrar
             * el rendimiento de los cambios de ruta.
             */
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        }),
    ],

    /** * @type {number} Porcentaje de transacciones de rendimiento a capturar.
     * 0.1 indica que se registra el 10% de las sesiones de usuario.
     */
    tracesSampleRate: parseFloat(process.env.VUE_APP_SENTRY_SAMPLE_RATE) || 0.1,

    /** * @type {boolean} Si es true, envía las 'props' de los componentes Vue 
     * junto con el reporte del error para facilitar el debug.
     */
    attachProps: true,

    /** * @type {boolean} Si es true, los errores capturados por Sentry
     * también se mostrarán en la consola del navegador.
     */
    logErrors: false,

    /** * @type {Array<RegExp>} Fallos de navegación benignos de vue-router que
     * NO deben reportarse a Sentry. Ocurren cuando un guard redirige la
     * navegación (p. ej. la inyección de contexto de cliente que reañade
     * `?search=<rut>&searchType=cliente_rut` a las rutas /apps/servicios*) o
     * cuando se navega a la ruta actual. La navegación sí se completa; el
     * rechazo de la promesa es esperado y sólo genera ruido (issues
     * automáticas). Se filtran por mensaje porque el rechazo puede escapar sin
     * capturarse antes de que el override de push/replace lo alcance.
     */
    ignoreErrors: [
        /Redirected when going from .* via a navigation guard/,
        /Navigation cancelled from .* with a new navigation/,
        /Avoided redundant navigation to current location/,
        "NavigationDuplicated",
    ],
});

Vue.component('pagination', require('laravel-vue-pagination'));

// Set this to false to prevent the production tip on Vue startup.
Vue.config.productionTip = false

const options = {
    color: '#62ca3f',
    failedColor: '#ff0000',
    thickness: '5px',
    transition: {
        speed: '0.2s',
        opacity: '0.6s',
        termination: 300
    },
    autoRevert: true,
    location: 'top',
    inverse: false
}

Vue.use(VueProgressBar, options)
/*
|---------------------------------------------------------------------
| Main Vue Instance
|---------------------------------------------------------------------
|
| Render the vue application on the <div id="app"></div> in index.html
|
| https://vuejs.org/v2/guide/instance.html
|
*/
const app = new Vue({
    i18n,
    vuetify,
    router,
    store,
    render: (h) => h(App)
}).$mount('#app')

// La app arrancó bien -> permitir que un futuro despliegue en esta misma sesión
// gatille una recuperación de una sola pasada.
clearChunkReloadGuardOnSuccess()

// Aviso de nueva versión disponible. Se arranca después del mount para no
// competir con la carga inicial; el primer chequeo es inmediato.
if (process.env.NODE_ENV != 'development') {
    startVersionCheck()
}

export default app
